import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, MessageCircle, ExternalLink, CheckCircle, XCircle, Ban, Undo, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { orders as ordersApi } from '@/utils/api'
import { useOrder } from '@/hooks/useOrders'
import { formatDateTime, getStatusLabel } from '@/utils/formatters'
import { cn } from '@/lib/utils'
import { useAuth } from '@/context/AuthContext'

interface OrderItem {
  id: number; product_name: string; article_number?: string; quantity: number; unit?: string
  notes?: string; is_checked?: boolean; checked_at?: string | null; excluded?: boolean
  exclusion_reason?: string | null; excluded_at?: string | null
  photo_urls?: string[]; supplier_links?: { name: string; url: string }[]
}

interface OrderDetail {
  id: number; project_name?: string; bauleiter_name?: string; status: string
  office_seen?: number
  created_at?: string; submitted_at?: string; updated_at?: string
  rejection_reason?: string; rejected_by?: string; rejected_at?: string
  items: OrderItem[]
  questions?: { id: number; item_id: number; question: string; answer?: string; asked_by: string }[]
}

const statusVariant = (s: string) => s === 'submitted' ? 'warning' as const : s === 'ordered' || s === 'completed' ? 'success' as const : s === 'rejected' ? 'destructive' as const : 'secondary' as const

export default function BestellungDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isAdmin } = useAuth()
  const [questionModal, setQuestionModal] = useState<{ open: boolean; item: OrderItem | null }>({ open: false, item: null })
  const [question, setQuestion] = useState('')
  const [rejectModal, setRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [deleteModal, setDeleteModal] = useState(false)
  const [excludeModal, setExcludeModal] = useState<{ open: boolean; itemIndex: number | null }>({ open: false, itemIndex: null })
  const [excludeReason, setExcludeReason] = useState('')

  const { data: order, isLoading } = useOrder(id)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['orders', id] })

  // Bugtracker #98: beim Öffnen als angeschaut markieren → Sidebar-Punkt zählt runter
  useEffect(() => {
    if (id && order && order.status === 'submitted' && !order.office_seen) {
      ordersApi.markSeen(id).catch(() => { /* best-effort */ })
    }
  }, [id, order?.status, order?.office_seen])

  const updateMutation = useMutation({
    mutationFn: (items: OrderItem[]) => ordersApi.update(id, { items }),
    onSuccess: () => invalidate(),
    onError: () => toast.error('Fehler beim Speichern'),
  })

  const completeMutation = useMutation({
    mutationFn: () => ordersApi.complete(id),
    onSuccess: () => { toast.success('Bestellung als bestellt markiert'); invalidate() },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler'),
  })

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => ordersApi.reject(id, reason),
    onSuccess: () => { toast.success('Bestellung abgelehnt'); setRejectModal(false); setRejectReason(''); invalidate() },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler'),
  })

  const sendQuestionMutation = useMutation({
    mutationFn: ({ itemId, text }: { itemId: number; text: string }) => ordersApi.sendQuestion(id, itemId, text),
    onSuccess: () => { toast.success('Rueckfrage gesendet'); setQuestionModal({ open: false, item: null }); setQuestion(''); invalidate() },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => ordersApi.remove(id),
    onSuccess: () => {
      toast.success('Bestellung gelöscht')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      navigate('/admin/bestellungen')
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler beim Löschen'),
  })

  const handleToggleItem = (idx: number) => {
    if (!order) return
    const items = [...order.items]
    items[idx] = { ...items[idx], is_checked: !items[idx].is_checked, checked_at: !items[idx].is_checked ? new Date().toISOString() : null }
    updateMutation.mutate(items)
  }

  const handleSendQuestion = () => {
    if (!question.trim() || !questionModal.item) return
    sendQuestionMutation.mutate({ itemId: questionModal.item.id, text: question })
  }

  const handleComplete = () => completeMutation.mutate()

  const handleReject = () => {
    if (!rejectReason.trim()) { toast.error('Bitte Ablehnungsgrund angeben'); return }
    rejectMutation.mutate(rejectReason)
  }

  const handleExcludeItem = () => {
    if (excludeModal.itemIndex === null || !order) return
    const items = [...order.items]
    items[excludeModal.itemIndex] = { ...items[excludeModal.itemIndex], excluded: true, exclusion_reason: excludeReason.trim() || null, excluded_at: new Date().toISOString() }
    updateMutation.mutate(items)
    setExcludeModal({ open: false, itemIndex: null })
    setExcludeReason('')
    toast.success('Position als "nicht bestellen" markiert')
  }

  const handleUnexcludeItem = (idx: number) => {
    if (!order) return
    const items = [...order.items]
    items[idx] = { ...items[idx], excluded: false, exclusion_reason: null, excluded_at: null }
    updateMutation.mutate(items)
    toast.success('Position wieder freigegeben')
  }

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>
  if (!order) return null

  const allChecked = order.items?.every(i => i.is_checked || i.excluded)
  const checkedCount = order.items?.filter(i => i.is_checked).length || 0
  const excludedCount = order.items?.filter(i => i.excluded).length || 0
  const totalItems = order.items?.length || 0
  const canComplete = allChecked && !['completed', 'partially_completed', 'rejected', 'archived'].includes(order.status)

  return (
    <div>
      <PageHeader title={order.project_name || 'Bestellung'} description={`Erstellt von ${order.bauleiter_name || '-'} am ${formatDateTime(order.created_at || order.submitted_at || order.updated_at)}`} actions={<Link to="/admin/bestellungen"><Button variant="outline"><ArrowLeft className="h-4 w-4" /> Zurück</Button></Link>} />

      {/* Status Bar */}
      <Card className="mb-6"><CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Badge variant={statusVariant(order.status)} className="px-4 py-1 text-base">{getStatusLabel(order.status)}</Badge>
            <span className="text-muted-foreground">{checkedCount}/{totalItems} bearbeitet{excludedCount > 0 && <span className="ml-2 text-orange-600">({excludedCount} nicht bestellen)</span>}</span>
          </div>
          <div className="flex items-center gap-2">
            {order.status === 'submitted' && <Button variant="destructive" onClick={() => setRejectModal(true)}><XCircle className="h-4 w-4" /> Ablehnen</Button>}
            {canComplete && <Button onClick={handleComplete} disabled={completeMutation.isPending}>{completeMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}<CheckCircle className="h-4 w-4" /> {excludedCount > 0 ? 'Teilweise abschliessen' : 'Abschliessen'}</Button>}
            {isAdmin && <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteModal(true)}><Trash2 className="h-4 w-4" /> Löschen</Button>}
          </div>
        </div>
        {order.status === 'rejected' && order.rejection_reason && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 dark:bg-red-950"><p className="text-sm text-red-800"><strong>Ablehnungsgrund:</strong> {order.rejection_reason}</p></div>
        )}
      </CardContent></Card>

      {/* Items */}
      <div className="space-y-4">
        {order.items?.map((item, index) => (
          <Card key={item.id} className={cn('transition-opacity', item.excluded && 'border-orange-200 bg-orange-50 opacity-60 dark:bg-orange-950', item.is_checked && !item.excluded && 'bg-muted/50 opacity-60')}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {item.excluded ? (
                  <div className="mt-1 flex h-6 w-6 items-center justify-center rounded border-2 border-orange-400 bg-orange-100"><Ban className="h-4 w-4 text-orange-600" /></div>
                ) : (
                  <button onClick={() => handleToggleItem(index)} className={cn('mt-1 flex h-6 w-6 items-center justify-center rounded border-2 transition-colors', item.is_checked ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 hover:border-primary-500')}>
                    {item.is_checked && <Check className="h-4 w-4" />}
                  </button>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className={cn('font-medium', item.excluded && 'text-orange-600 line-through', item.is_checked && !item.excluded && 'text-muted-foreground line-through')}>{item.product_name}</h3>
                      {item.article_number && <p className="text-sm text-muted-foreground">Art.Nr.: {item.article_number}</p>}
                      {item.excluded && <p className="mt-1 text-sm font-medium text-orange-600">Nicht bestellen{item.exclusion_reason && ` - ${item.exclusion_reason}`}</p>}
                    </div>
                    <p className={cn('text-lg font-semibold', item.excluded && 'text-orange-600')}>{item.quantity} {item.unit}</p>
                  </div>
                  {item.notes && <p className="mt-2 rounded bg-yellow-50 p-2 text-sm dark:bg-yellow-950">Notiz: {item.notes}</p>}
                  {item.photo_urls && item.photo_urls.length > 0 && (
                    <div className="mt-3 flex gap-2">{item.photo_urls.map((url, i) => <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block h-16 w-16 overflow-hidden rounded border hover:border-primary-500"><img src={url} alt="" className="h-full w-full object-cover" /></a>)}</div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.supplier_links?.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded bg-muted px-3 py-1 text-sm transition-colors hover:bg-muted/80">{link.name}<ExternalLink className="h-3 w-3" /></a>)}
                    <Button variant="ghost" size="sm" onClick={() => setQuestionModal({ open: true, item })}><MessageCircle className="h-4 w-4" /> Rueckfrage</Button>
                    {!['completed','partially_completed','rejected'].includes(order.status) && (
                      item.excluded ? <Button variant="ghost" size="sm" className="text-orange-600" onClick={() => handleUnexcludeItem(index)}><Undo className="h-4 w-4" /> Freigeben</Button>
                        : <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setExcludeModal({ open: true, itemIndex: index })}><Ban className="h-4 w-4" /> Nicht bestellen</Button>
                    )}
                  </div>
                  {order.questions?.filter(q => q.item_id === item.id).map(q => (
                    <div key={q.id} className="mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-950">
                      <p className="text-sm"><span className="font-medium text-blue-800">Frage ({q.asked_by}):</span> {q.question}</p>
                      {q.answer ? <p className="mt-1 text-sm"><span className="font-medium text-green-800">Antwort:</span> {q.answer}</p> : <p className="mt-1 text-sm italic text-muted-foreground">Wartet auf Antwort...</p>}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Question Dialog */}
      <Dialog open={questionModal.open} onOpenChange={(o) => !o && setQuestionModal({ open: false, item: null })}>
        <DialogContent><DialogHeader><DialogTitle>Rueckfrage zu: {questionModal.item?.product_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Frage wird an den Bauleiter gesendet.</p>
          <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm" placeholder="Ihre Frage..." value={question} onChange={(e) => setQuestion(e.target.value)} />
          <DialogFooter><Button variant="outline" onClick={() => setQuestionModal({ open: false, item: null })}>Abbrechen</Button><Button onClick={handleSendQuestion} disabled={!question.trim() || sendQuestionMutation.isPending}>{sendQuestionMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Senden</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectModal} onOpenChange={setRejectModal}>
        <DialogContent><DialogHeader><DialogTitle>Bestellung ablehnen</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Der Bauleiter wird informiert.</p>
          <div className="space-y-2"><label className="text-sm font-medium">Grund der Ablehnung</label><textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm" placeholder="Grund angeben..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => setRejectModal(false)}>Abbrechen</Button><Button variant="destructive" onClick={handleReject} disabled={!rejectReason.trim() || rejectMutation.isPending}>{rejectMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Ablehnen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exclude Dialog */}
      <Dialog open={excludeModal.open} onOpenChange={(o) => { if (!o) { setExcludeModal({ open: false, itemIndex: null }); setExcludeReason('') } }}>
        <DialogContent><DialogHeader><DialogTitle>Position nicht bestellen</DialogTitle></DialogHeader>
          {excludeModal.itemIndex !== null && order.items?.[excludeModal.itemIndex] && (
            <div className="rounded-lg bg-muted p-3"><p className="font-medium">{order.items[excludeModal.itemIndex].product_name}</p><p className="text-sm text-muted-foreground">{order.items[excludeModal.itemIndex].quantity} {order.items[excludeModal.itemIndex].unit}</p></div>
          )}
          <div className="space-y-2"><label className="text-sm font-medium">Grund (optional)</label><textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm" placeholder="z.B. Nicht lieferbar..." value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)} /></div>
          <DialogFooter><Button variant="outline" onClick={() => { setExcludeModal({ open: false, itemIndex: null }); setExcludeReason('') }}>Abbrechen</Button><Button variant="default" onClick={handleExcludeItem}>Nicht bestellen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog (nur Admin, Bugtracker #97) */}
      <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bestellung löschen?</DialogTitle>
            <DialogDescription>
              Die Bestellung{order.project_name ? ` „${order.project_name}"` : ''} wird unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal(false)} disabled={deleteMutation.isPending}>Abbrechen</Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>{deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}<Trash2 className="h-4 w-4" /> Endgültig löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
